"use client";

import React, { useState, useMemo, useCallback } from "react";
import { CodeHighlight } from "@/components/CodeHighlight";
import {
  Columns3,
  Network,
  Table2,
  FileCode2,
  Search,
  Copy,
  Check,
  Upload,
  Wand2,
  Download,
  ChevronRight,
  ChevronDown,
  Sparkles,
  PenLine,
  ArrowUpDown,
  AlertTriangle,
} from "lucide-react";

type JsonPrimitive = string | number | boolean | null;
type JsonObject = { [key: string]: JsonValue };
type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;
type ExplorerView = "columns" | "tree" | "table" | "typescript";

const IDENTIFIER_RE = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

const PRESETS: Array<{ id: string; label: string; raw: string }> = [
  {
    id: "graphql-shop",
    label: "Verschachtelte API (6 Ebenen)",
    raw: JSON.stringify(
      {
        requestId: "req_9f82c1a0",
        generatedAt: "2026-10-27T08:14:22Z",
        data: {
          organization: {
            slug: "nordlicht-technik",
            region: "eu-central-1",
            store: {
              currency: "EUR",
              orders: [
                {
                  id: "ORD-2026-8841",
                  createdAt: "2026-10-26T14:20:00Z",
                  status: "shipped",
                  customer: {
                    name: "Hannah Bergmann",
                    email: "h.bergmann@example.de",
                    address: { city: "Hamburg", postalCode: "20457", country: "DE" },
                  },
                  payment: { method: "sepa_debit", amountEur: 249.9, paid: true, discountCode: "HERBST10" },
                  shipping: {
                    carrier: "DHL",
                    trackingCode: "003404341610940129",
                    checkpoints: [
                      { hub: "Bremen GVZ", timestamp: "2026-10-26T19:05:00Z", ok: true },
                      { hub: "Hamburg Süd", timestamp: "2026-10-27T05:42:00Z", ok: true },
                    ],
                  },
                  items: [
                    { sku: "NVME-2TB-PRO", title: "M.2 NVMe SSD 2 TB", qty: 1, unitEur: 169.9 },
                    { sku: "USB4-CABLE-1M", title: "USB4 40 Gbit/s Kabel", qty: 2, unitEur: 40.0 },
                  ],
                },
                {
                  id: "ORD-2026-8842",
                  createdAt: "2026-10-26T16:48:10Z",
                  status: "payment_failed",
                  customer: {
                    name: "Markus Lehner",
                    email: "m.lehner@example.at",
                    address: { city: "Wien", postalCode: "1010", country: "AT" },
                  },
                  payment: { method: "card", amountEur: 89.0, paid: false, discountCode: null },
                  shipping: {
                    carrier: "Post AT",
                    trackingCode: null,
                    checkpoints: [],
                  },
                  items: [
                    { sku: "ZIGBEE-STICK-E", title: "Thread & Zigbee USB-Koordinator", qty: 1, unitEur: 89.0 },
                  ],
                },
                {
                  id: "ORD-2026-8843",
                  createdAt: "2026-10-27T07:03:55Z",
                  status: "processing",
                  customer: {
                    name: "Clara Weiß",
                    email: "clara.weiss@example.ch",
                    address: { city: "Zürich", postalCode: "8005", country: "CH" },
                  },
                  payment: { method: "invoice", amountEur: 518.5, paid: true, discountCode: "B2B-NET30" },
                  shipping: {
                    carrier: "SwissPost",
                    trackingCode: "99.00.128491.000412",
                    checkpoints: [
                      { hub: "Basel Grenze", timestamp: "2026-10-27T07:50:00Z", ok: true },
                    ],
                  },
                  items: [
                    { sku: "MINI-PC-N100", title: "Lautloser Homelab Mini-Server", qty: 1, unitEur: 518.5 },
                  ],
                },
              ],
            },
          },
        },
      },
      null,
      2
    ),
  },
  {
    id: "iot-dump",
    label: "Home-Assistant / IoT-State",
    raw: JSON.stringify(
      {
        home: {
          timezone: "Europe/Berlin",
          bridgeVersion: "2026.10.2",
          devices: [
            {
              entityId: "sensor.dach_pv_leistung",
              room: "Dachboden",
              stateWatts: 3420,
              online: true,
              telemetry: { voltage: 233.4, tempC: 41.2, firmware: "1.14.0" },
            },
            {
              entityId: "climate.arbeitszimmer_heizung",
              room: "Arbeitszimmer",
              stateWatts: 0,
              online: true,
              telemetry: { voltage: 3.1, tempC: 21.4, firmware: "2.04.8", windowOpen: false },
            },
            {
              entityId: "sensor.keller_feuchte",
              room: "Keller",
              stateWatts: 0,
              online: false,
              telemetry: { voltage: 2.55, tempC: 14.8, firmware: "1.09.2" },
            },
          ],
        },
      },
      null,
      2
    ),
  },
  {
    id: "broken-js",
    label: "Kaputtes JS-Objekt (Reparatur-Test)",
    raw: `// Typischer Konsolen- oder Config-Schnipsel mit Fehlern:
{
  service: 'auth-gateway',
  retries: 3,
  endpoints: [
    { host: 'api.intern.local', port: 8443, active: true, },
    { host: 'backup.intern.local', port: 8444, active: false, },
  ],
}`,
  },
  {
    id: "german-csv",
    label: "Semikolon-CSV (Excel-Import)",
    raw: `Belegnummer;Datum;Kategorie;Betrag_EUR;Bezahlt;Ansprechpartner
RE-2026-101;2026-10-02;Cloud-Hosting;42.80;true;"Julia Krüger"
RE-2026-102;2026-10-09;Domain-Verlängerung;14.90;true;"Lukas Weber"
RE-2026-103;2026-10-18;Hardware-Testgerät;319.00;false;"Stefan Möller"
RE-2026-104;2026-10-24;Backup-Speicher;19.50;true;"Nina Hofmann"`,
  },
];

function isJsonObject(val: JsonValue | undefined): val is JsonObject {
  return typeof val === "object" && val !== null && !Array.isArray(val);
}

function formatJsPath(segments: Array<string | number>, optionalChain = false): string {
  if (segments.length === 0) return "data";
  let out = "data";
  for (const seg of segments) {
    if (typeof seg === "number") {
      out += optionalChain ? `?.[${seg}]` : `[${seg}]`;
    } else if (IDENTIFIER_RE.test(seg)) {
      out += optionalChain ? `?.${seg}` : `.${seg}`;
    } else {
      const escaped = seg.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
      out += optionalChain ? `?.["${escaped}"]` : `["${escaped}"]`;
    }
  }
  return out;
}

function getValueAtPath(root: JsonValue, path: Array<string | number>): JsonValue | undefined {
  let curr: JsonValue | undefined = root;
  for (const seg of path) {
    if (curr === null || curr === undefined) return undefined;
    if (typeof seg === "number" && Array.isArray(curr)) {
      curr = curr[seg];
    } else if (typeof seg === "string" && isJsonObject(curr)) {
      curr = curr[seg];
    } else {
      return undefined;
    }
  }
  return curr;
}

function summarizeNodeBadge(val: JsonValue): { label: string; colorClass: string } {
  if (val === null) {
    return { label: "null", colorClass: "bg-neutral-200/80 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-400" };
  }
  if (Array.isArray(val)) {
    return {
      label: `[${val.length}]`,
      colorClass: "bg-amber-500/15 text-amber-800 dark:text-amber-300",
    };
  }
  if (typeof val === "object") {
    return {
      label: `{${Object.keys(val).length}}`,
      colorClass: "bg-sky-500/15 text-sky-800 dark:text-sky-300",
    };
  }
  if (typeof val === "string") {
    const short = val.length > 18 ? `"${val.slice(0, 16)}…"` : `"${val}"`;
    return { label: short, colorClass: "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300" };
  }
  if (typeof val === "boolean") {
    return {
      label: val ? "true" : "false",
      colorClass: "bg-purple-500/15 text-purple-800 dark:text-purple-300",
    };
  }
  return {
    label: String(val),
    colorClass: "bg-rose-500/15 text-rose-800 dark:text-rose-300",
  };
}

function parseCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      cells.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  cells.push(cur.trim());
  return cells;
}

function tryParseCsv(raw: string): JsonValue[] | null {
  const trimmed = raw.replace(/^\uFEFF/, "").trim();
  const lines = trimmed.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) return null;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) return null;

  const first = lines[0];
  const semiCount = (first.match(/;/g) || []).length;
  const commaCount = (first.match(/,/g) || []).length;
  const tabCount = (first.match(/\t/g) || []).length;
  const maxSep = Math.max(semiCount, commaCount, tabCount);
  if (maxSep === 0) return null;

  const delimiter = tabCount === maxSep ? "\t" : semiCount >= commaCount ? ";" : ",";
  const headers = parseCsvLine(first, delimiter).map((h, idx) => h || `spalte_${idx + 1}`);
  if (headers.length < 2) return null;

  return lines.slice(1).map((line) => {
    const values = parseCsvLine(line, delimiter);
    const rowObj: JsonObject = {};
    headers.forEach((h, idx) => {
      const cell = values[idx] ?? "";
      if (cell === "") {
        rowObj[h] = null;
      } else if (cell === "true" || cell === "false") {
        rowObj[h] = cell === "true";
      } else if (/^-?\d+(\.\d+)?$/.test(cell)) {
        rowObj[h] = Number(cell);
      } else {
        rowObj[h] = cell;
      }
    });
    return rowObj;
  });
}

function repairLooseJson(input: string): string {
  let s = input.replace(/^\uFEFF/, "").trim();
  // Remove // line comments and /* block comments */ outside strings
  s = s.replace(/^\s*\/\/.*$/gm, "");
  s = s.replace(/\/\*[\s\S]*?\*\//g, "");
  // Convert single-quoted strings to double-quoted strings
  s = s.replace(/'([^'\\]*(?:\\.[^'\\]*)*)'/g, (_, inner: string) => {
    return `"${inner.replace(/"/g, '\\"')}"`;
  });
  // Quote unquoted object keys: { foo_1: ... , bar: ... }
  s = s.replace(/([{,]\s*)([A-Za-z_$][A-Za-z0-9_$]*)(\s*:)/g, '$1"$2"$3');
  // Remove trailing commas before } or ]
  s = s.replace(/,(\s*[}\]])/g, "$1");
  return s.trim();
}

function parseSmartInput(raw: string): {
  data: JsonValue | null;
  sourceFormat: "json" | "ndjson" | "csv" | "repaired-js";
  error: string | null;
  canRepair: boolean;
} {
  const cleaned = raw.replace(/^\uFEFF/, "").trim();
  if (!cleaned) {
    return { data: null, sourceFormat: "json", error: "Bitte füge JSON, NDJSON oder CSV ein.", canRepair: false };
  }

  // 1. Strict RFC 8259 JSON
  try {
    const parsed = JSON.parse(cleaned) as JsonValue;
    return { data: parsed, sourceFormat: "json", error: null, canRepair: false };
  } catch (strictErr) {
    // 2. NDJSON (.jsonl): one JSON object per line
    const lines = cleaned.split(/\r?\n/).filter((l) => l.trim().startsWith("{"));
    if (lines.length >= 2) {
      try {
        const arr = lines.map((l) => JSON.parse(l) as JsonValue);
        return { data: arr, sourceFormat: "ndjson", error: null, canRepair: false };
      } catch {
        // continue
      }
    }

    // 3. CSV with ; / , / \t auto-detection
    const csvRows = tryParseCsv(cleaned);
    if (csvRows) {
      return { data: csvRows, sourceFormat: "csv", error: null, canRepair: false };
    }

    // 4. Check if loose JS repair succeeds
    try {
      const repaired = repairLooseJson(cleaned);
      JSON.parse(repaired);
      return {
        data: null,
        sourceFormat: "json",
        error:
          strictErr instanceof Error
            ? `Ungültiges Standard-JSON (${strictErr.message}). Ein Klick auf „Tolerant reparieren“ behebt Kommentare, einfache Anführungszeichen und nachgestellte Kommas.`
            : "Ungültiges JSON – automatische Reparatur verfügbar.",
        canRepair: true,
      };
    } catch {
      return {
        data: null,
        sourceFormat: "json",
        error:
          strictErr instanceof Error
            ? `Syntaxfehler im JSON: ${strictErr.message}`
            : "Eingabe konnte weder als JSON noch als CSV gelesen werden.",
        canRepair: false,
      };
    }
  }
}

interface SearchMatch {
  path: Array<string | number>;
  pathLabel: string;
  preview: string;
}

function searchJsonPaths(root: JsonValue, query: string, maxResults = 150): SearchMatch[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const matches: SearchMatch[] = [];

  const visit = (val: JsonValue, path: Array<string | number>) => {
    if (matches.length >= maxResults) return;
    const lastSeg = path.length > 0 ? String(path[path.length - 1]).toLowerCase() : "";
    const keyMatched = lastSeg.includes(q);

    if (val === null || typeof val !== "object") {
      const valStr = String(val);
      if (keyMatched || valStr.toLowerCase().includes(q)) {
        matches.push({
          path,
          pathLabel: formatJsPath(path),
          preview: typeof val === "string" ? `"${val}"` : valStr,
        });
      }
      return;
    }

    if (keyMatched) {
      matches.push({
        path,
        pathLabel: formatJsPath(path),
        preview: Array.isArray(val) ? `Array(${val.length})` : `Objekt(${Object.keys(val).length} Felder)`,
      });
    }

    if (Array.isArray(val)) {
      val.forEach((child, idx) => visit(child, [...path, idx]));
    } else {
      Object.entries(val).forEach(([k, child]) => visit(child, [...path, k]));
    }
  };

  visit(root, []);
  return matches;
}

interface DetectedTable {
  id: string;
  label: string;
  path: Array<string | number>;
  rows: JsonObject[];
}

function findObjectArrays(root: JsonValue): DetectedTable[] {
  const found: DetectedTable[] = [];

  const visit = (val: JsonValue, path: Array<string | number>) => {
    if (found.length >= 25) return;
    if (Array.isArray(val)) {
      const objRows = val.filter((item): item is JsonObject => isJsonObject(item));
      if (objRows.length > 0) {
        const label = path.length === 0 ? "Wurzel-Array" : formatJsPath(path);
        found.push({
          id: label,
          label: `${label} (${objRows.length} Zeilen)`,
          path,
          rows: objRows,
        });
      }
      val.slice(0, 8).forEach((item, idx) => visit(item, [...path, idx]));
    } else if (isJsonObject(val)) {
      Object.entries(val).forEach(([k, child]) => visit(child, [...path, k]));
    }
  };

  visit(root, []);
  return found;
}

function flattenObjectRow(obj: JsonObject, prefix = "", depth = 0): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [k, v] of Object.entries(obj)) {
    const colKey = prefix ? `${prefix}.${k}` : k;
    if (v === null || typeof v !== "object") {
      out[colKey] = v;
    } else if (Array.isArray(v)) {
      if (v.every((item) => item === null || typeof item !== "object")) {
        out[colKey] = v.join(", ");
      } else {
        out[colKey] = `[${v.length} Einträge]`;
      }
    } else if (depth < 3) {
      Object.assign(out, flattenObjectRow(v, colKey, depth + 1));
    } else {
      out[colKey] = JSON.stringify(v);
    }
  }
  return out;
}

function inferTypeScriptInterfaces(root: JsonValue, rootName = "RootPayload"): string {
  const interfaces: string[] = [];
  const usedNames = new Set<string>();

  const toPascal = (raw: string): string => {
    const cleaned = raw
      .replace(/[^A-Za-z0-9]+/g, " ")
      .trim()
      .split(/\s+/)
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
      .join("");
    const base = cleaned || "NestedItem";
    if (!usedNames.has(base)) {
      usedNames.add(base);
      return base;
    }
    let n = 2;
    while (usedNames.has(`${base}${n}`)) n++;
    const next = `${base}${n}`;
    usedNames.add(next);
    return next;
  };

  const inferType = (val: JsonValue, hintName: string): string => {
    if (val === null) return "null";
    if (typeof val === "string") return "string";
    if (typeof val === "number") return "number";
    if (typeof val === "boolean") return "boolean";

    if (Array.isArray(val)) {
      if (val.length === 0) return "unknown[]";
      const objItems = val.filter((item): item is JsonObject => isJsonObject(item));
      if (objItems.length > 0 && objItems.length === val.length) {
        const mergedName = toPascal(hintName.replace(/s$/i, "") || "Item");
        buildMergedInterface(objItems, mergedName);
        return `${mergedName}[]`;
      }
      const elemTypes = Array.from(new Set(val.map((item) => inferType(item, hintName))));
      return elemTypes.length === 1 ? `${elemTypes[0]}[]` : `Array<${elemTypes.join(" | ")}>`;
    }

    const ifaceName = toPascal(hintName);
    buildMergedInterface([val], ifaceName);
    return ifaceName;
  };

  const buildMergedInterface = (objects: JsonObject[], ifaceName: string) => {
    const allKeys = new Set<string>();
    objects.forEach((o) => Object.keys(o).forEach((k) => allKeys.add(k)));

    const propLines: string[] = [];
    allKeys.forEach((key) => {
      let presentCount = 0;
      const typesForKey = new Set<string>();
      objects.forEach((o) => {
        if (Object.prototype.hasOwnProperty.call(o, key)) {
          presentCount++;
          typesForKey.add(inferType(o[key], key));
        }
      });
      const optional = presentCount < objects.length ? "?" : "";
      const safeKey = IDENTIFIER_RE.test(key) ? key : JSON.stringify(key);
      const union = Array.from(typesForKey).join(" | ") || "unknown";
      propLines.push(`  ${safeKey}${optional}: ${union};`);
    });

    interfaces.push(
      `export interface ${ifaceName} {\n${propLines.join("\n") || "  [key: string]: unknown;"}\n}`
    );
  };

  if (isJsonObject(root)) {
    const topName = toPascal(rootName);
    buildMergedInterface([root], topName);
  } else if (Array.isArray(root)) {
    const elemType = inferType(root, "RootItem");
    interfaces.push(`export type ${toPascal(rootName)} = ${elemType};`);
  } else {
    interfaces.push(`export type ${toPascal(rootName)} = ${inferType(root, rootName)};`);
  }

  return interfaces.reverse().join("\n\n");
}

function CollapsibleJsonTree({
  value,
  path,
  selectedPath,
  onSelectPath,
  defaultExpandDepth,
}: {
  value: JsonValue;
  path: Array<string | number>;
  selectedPath: Array<string | number>;
  onSelectPath: (p: Array<string | number>) => void;
  defaultExpandDepth: number;
}) {
  const [open, setOpen] = useState(path.length < defaultExpandDepth);
  const isSelected =
    path.length === selectedPath.length && path.every((seg, i) => seg === selectedPath[i]);

  if (value === null || typeof value !== "object") {
    const badge = summarizeNodeBadge(value);
    return (
      <span className={`rounded px-1.5 py-0.5 font-mono text-xs ${badge.colorClass}`}>
        {badge.label}
      </span>
    );
  }

  const entries: Array<[string | number, JsonValue]> = Array.isArray(value)
    ? value.map((v, idx) => [idx, v])
    : Object.entries(value);

  return (
    <div className="font-mono text-xs">
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-neutral-600 hover:bg-neutral-200/70 dark:text-neutral-300 dark:hover:bg-neutral-800 cursor-pointer"
        >
          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          <span className="font-semibold text-amber-700 dark:text-amber-300">
            {Array.isArray(value) ? `Array [${entries.length}]` : `Objekt {${entries.length}}`}
          </span>
        </button>
        <button
          type="button"
          onClick={() => onSelectPath(path)}
          className={`rounded px-1.5 py-0.5 text-[10px] transition-colors cursor-pointer ${
            isSelected
              ? "bg-amber-500 text-white"
              : "text-neutral-400 hover:bg-neutral-200/60 hover:text-neutral-800 dark:hover:bg-neutral-800 dark:hover:text-neutral-200"
          }`}
        >
          Pfad wählen
        </button>
      </div>

      {open && entries.length > 0 && (
        <ul className="mt-1 ml-3 space-y-1 border-l border-neutral-200/90 pl-3 dark:border-neutral-800">
          {entries.slice(0, 150).map(([k, child]) => {
            const childPath = [...path, k];
            const childIsComplex = child !== null && typeof child === "object";
            return (
              <li key={String(k)} className="leading-relaxed">
                <div className="flex flex-wrap items-start gap-1.5">
                  <button
                    type="button"
                    onClick={() => onSelectPath(childPath)}
                    className="font-semibold text-neutral-800 hover:text-amber-600 dark:text-neutral-200 dark:hover:text-amber-400 cursor-pointer"
                  >
                    {typeof k === "number" ? `[${k}]` : k}:
                  </button>
                  <CollapsibleJsonTree
                    value={child}
                    path={childPath}
                    selectedPath={selectedPath}
                    onSelectPath={onSelectPath}
                    defaultExpandDepth={defaultExpandDepth}
                  />
                  {!childIsComplex && null}
                </div>
              </li>
            );
          })}
          {entries.length > 150 && (
            <li className="text-[11px] text-neutral-400">
              … {entries.length - 150} weitere Einträge (nutze die Pfad-Suche oder Auto-Tabelle)
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function JsonExplorerPlayground() {
  const [rawInput, setRawInput] = useState<string>(PRESETS[0].raw);
  const [showRawEditor, setShowRawEditor] = useState<boolean>(false);
  const [activeView, setActiveView] = useState<ExplorerView>("columns");
  const [selectedPath, setSelectedPath] = useState<Array<string | number>>([
    "data",
    "organization",
    "store",
    "orders",
    0,
  ]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedTableIdx, setSelectedTableIdx] = useState<number>(0);
  const [tableFilter, setTableFilter] = useState<string>("");
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [copiedKind, setCopiedKind] = useState<string | null>(null);

  const parsedResult = useMemo(() => parseSmartInput(rawInput), [rawInput]);
  const rootData = parsedResult.data;

  const selectedValue = useMemo(() => {
    if (rootData === null) return undefined;
    const val = getValueAtPath(rootData, selectedPath);
    return val !== undefined ? val : rootData;
  }, [rootData, selectedPath]);

  const effectivePath = useMemo(() => {
    if (rootData === null) return [];
    return getValueAtPath(rootData, selectedPath) !== undefined ? selectedPath : [];
  }, [rootData, selectedPath]);

  const searchMatches = useMemo(() => {
    if (rootData === null || !searchQuery.trim()) return [];
    return searchJsonPaths(rootData, searchQuery);
  }, [rootData, searchQuery]);

  const detectedTables = useMemo(() => {
    if (rootData === null) return [];
    return findObjectArrays(rootData);
  }, [rootData]);

  const activeTable = detectedTables[selectedTableIdx] ?? detectedTables[0] ?? null;

  const flattenedTable = useMemo(() => {
    if (!activeTable) return { columns: [] as string[], rows: [] as Array<Record<string, string | number | boolean | null>> };
    const flatRows = activeTable.rows.map((r) => flattenObjectRow(r));
    const colSet = new Set<string>();
    flatRows.forEach((r) => Object.keys(r).forEach((k) => colSet.add(k)));
    const columns = Array.from(colSet);

    const q = tableFilter.trim().toLowerCase();
    let filtered = q
      ? flatRows.filter((r) =>
          columns.some((c) => String(r[c] ?? "").toLowerCase().includes(q))
        )
      : flatRows;

    if (sortCol && columns.includes(sortCol)) {
      filtered = [...filtered].sort((a, b) => {
        const va = a[sortCol];
        const vb = b[sortCol];
        if (va === vb) return 0;
        if (va === null || va === undefined) return 1;
        if (vb === null || vb === undefined) return -1;
        if (typeof va === "number" && typeof vb === "number") {
          return sortAsc ? va - vb : vb - va;
        }
        return sortAsc
          ? String(va).localeCompare(String(vb), "de")
          : String(vb).localeCompare(String(va), "de");
      });
    }

    return { columns, rows: filtered };
  }, [activeTable, tableFilter, sortCol, sortAsc]);

  const generatedTs = useMemo(() => {
    if (selectedValue === undefined) return "// Keine gültigen Daten geladen";
    const nameHint =
      effectivePath.length > 0 ? String(effectivePath[effectivePath.length - 1]) : "RootPayload";
    return inferTypeScriptInterfaces(selectedValue, nameHint);
  }, [selectedValue, effectivePath]);

  // Miller Columns levels derived from rootData + effectivePath
  const millerColumns = useMemo(() => {
    if (rootData === null || typeof rootData !== "object") return [];
    const cols: Array<{
      depth: number;
      title: string;
      entries: Array<{ key: string | number; value: JsonValue }>;
      activeKey: string | number | undefined;
    }> = [];

    let cursor: JsonValue = rootData;
    for (let depth = 0; depth <= effectivePath.length; depth++) {
      if (cursor === null || typeof cursor !== "object") break;
      const entries = Array.isArray(cursor)
        ? cursor.map((v, idx) => ({ key: idx as string | number, value: v }))
        : Object.entries(cursor).map(([k, v]) => ({ key: k as string | number, value: v }));

      const activeKey = effectivePath[depth];
      cols.push({
        depth,
        title: depth === 0 ? "Wurzel (Ebene 0)" : `${String(effectivePath[depth - 1])} (Ebene ${depth})`,
        entries,
        activeKey,
      });

      if (activeKey === undefined) break;
      const next = getValueAtPath(cursor, [activeKey]);
      if (next === undefined) break;
      cursor = next;
    }

    return cols;
  }, [rootData, effectivePath]);

  const triggerCopy = useCallback(async (text: string, id: string) => {
    await navigator.clipboard?.writeText(text);
    setCopiedKind(id);
    setTimeout(() => setCopiedKind(null), 1800);
  }, []);

  const handleRepairAndFormat = () => {
    try {
      const repaired = repairLooseJson(rawInput);
      const obj = JSON.parse(repaired) as JsonValue;
      setRawInput(JSON.stringify(obj, null, 2));
      setSelectedPath([]);
    } catch {
      // ignore if unrepairable
    }
  };

  const handleFormatJson = () => {
    if (rootData !== null) {
      setRawInput(JSON.stringify(rootData, null, 2));
    }
  };

  const handleFileUpload = async (file?: File | null) => {
    if (!file) return;
    const text = await file.text();
    setRawInput(text);
    setSelectedPath([]);
    setSelectedTableIdx(0);
  };

  const exportFlattenedCsv = () => {
    if (flattenedTable.columns.length === 0) return;
    const escapeCell = (val: string | number | boolean | null | undefined) => {
      if (val === null || val === undefined) return "";
      const str = String(val);
      if (/[;"\n\r]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };
    const headerLine = flattenedTable.columns.map(escapeCell).join(";");
    const bodyLines = flattenedTable.rows.map((r) =>
      flattenedTable.columns.map((c) => escapeCell(r[c])).join(";")
    );
    // Include UTF-8 BOM so German Microsoft Excel opens umlauts and columns cleanly
    const csvContent = "\uFEFF" + [headerLine, ...bodyLines].join("\r\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "json-tabelle-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Top Preset & Input Controls Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-2xl border border-neutral-200/80 bg-neutral-50/80 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-xs font-semibold text-neutral-500 dark:text-neutral-400">
            Beispiele:
          </span>
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setRawInput(p.raw);
                setSelectedPath(
                  p.id === "graphql-shop" ? ["data", "organization", "store", "orders", 0] : []
                );
                setSelectedTableIdx(0);
                if (p.id === "broken-js") setShowRawEditor(true);
              }}
              className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 transition-colors hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700 cursor-pointer"
            >
              <Sparkles className="h-3 w-3 text-amber-500" />
              <span>{p.label}</span>
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setShowRawEditor((v) => !v)}
            className={`inline-flex min-h-[38px] items-center gap-1.5 rounded-xl border px-3 py-1.5 text-xs font-semibold transition-colors cursor-pointer ${
              showRawEditor
                ? "border-amber-500/40 bg-amber-500/15 text-amber-800 dark:text-amber-300"
                : "border-neutral-200/90 bg-white text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
            }`}
          >
            <PenLine className="h-3.5 w-3.5" />
            <span>{showRawEditor ? "Rohdaten ausblenden" : "Eigenes JSON / CSV einfügen"}</span>
          </button>

          <label className="inline-flex min-h-[38px] cursor-pointer items-center gap-1.5 rounded-xl border border-neutral-200/90 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
            <Upload className="h-3.5 w-3.5" />
            <span>Datei öffnen (.json / .csv)</span>
            <input
              type="file"
              accept=".json,.jsonl,.csv,.txt,application/json,text/csv"
              onChange={(e) => void handleFileUpload(e.target.files?.[0])}
              className="sr-only"
            />
          </label>
        </div>
      </div>

      {/* Collapsible Raw Input Area */}
      {(showRawEditor || parsedResult.error) && (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void handleFileUpload(e.dataTransfer.files?.[0]);
          }}
          className="rounded-2xl border border-neutral-200/90 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
        >
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label
              htmlFor="json-raw-textarea"
              className="text-xs font-bold text-neutral-700 dark:text-neutral-300"
            >
              Rohdaten-Eingabe (JSON, NDJSON, JavaScript-Objekt oder CSV – bleibt zu 100 % lokal im Browser)
            </label>
            <div className="flex flex-wrap gap-2">
              {parsedResult.canRepair && (
                <button
                  type="button"
                  onClick={handleRepairAndFormat}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 cursor-pointer"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  <span>Tolerant reparieren &amp; formatieren</span>
                </button>
              )}
              {rootData !== null && (
                <button
                  type="button"
                  onClick={handleFormatJson}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border border-neutral-300 bg-neutral-50 px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 cursor-pointer"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  <span>Als sauberes JSON formatieren</span>
                </button>
              )}
            </div>
          </div>

          <textarea
            id="json-raw-textarea"
            rows={8}
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            spellCheck={false}
            className="w-full rounded-xl border border-neutral-200 bg-neutral-50 p-3 font-mono text-xs text-neutral-900 focus:border-amber-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-100"
          />

          {parsedResult.error && (
            <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-900 dark:text-amber-200">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <span>{parsedResult.error}</span>
              </div>
              {parsedResult.canRepair && (
                <button
                  type="button"
                  onClick={handleRepairAndFormat}
                  className="inline-flex min-h-[38px] shrink-0 items-center gap-1.5 rounded-xl bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-amber-700 cursor-pointer"
                >
                  <Wand2 className="h-3.5 w-3.5" />
                  <span>Jetzt automatisch reparieren</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Selected Breadcrumb Path & Instant Copy Bar */}
      {rootData !== null && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/[0.04]">
          <div className="flex flex-wrap items-center gap-1 font-mono text-xs">
            <span className="mr-1 font-sans text-xs font-semibold text-neutral-500 dark:text-neutral-400">
              Aktueller Pfad:
            </span>
            <button
              type="button"
              onClick={() => setSelectedPath([])}
              className={`rounded-md px-2 py-0.5 font-bold transition-colors cursor-pointer ${
                effectivePath.length === 0
                  ? "bg-amber-600 text-white"
                  : "bg-white/80 text-neutral-700 hover:bg-white dark:bg-neutral-900 dark:text-neutral-300"
              }`}
            >
              data
            </button>
            {effectivePath.map((seg, idx) => {
              const isLast = idx === effectivePath.length - 1;
              return (
                <React.Fragment key={`${idx}-${String(seg)}`}>
                  <ChevronRight className="h-3.5 w-3.5 text-neutral-400" />
                  <button
                    type="button"
                    onClick={() => setSelectedPath(effectivePath.slice(0, idx + 1))}
                    className={`rounded-md px-2 py-0.5 font-bold transition-colors cursor-pointer ${
                      isLast
                        ? "bg-amber-600 text-white"
                        : "bg-white/80 text-neutral-700 hover:bg-white dark:bg-neutral-900 dark:text-neutral-300"
                    }`}
                  >
                    {typeof seg === "number" ? `[${seg}]` : seg}
                  </button>
                </React.Fragment>
              );
            })}
            {parsedResult.sourceFormat !== "json" && (
              <span className="ml-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-sans text-[10.5px] font-semibold text-emerald-700 dark:text-emerald-300">
                Automatisch aus {parsedResult.sourceFormat.toUpperCase()} konvertiert
              </span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => void triggerCopy(formatJsPath(effectivePath, false), "path")}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-800 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-200 cursor-pointer"
            >
              {copiedKind === "path" ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span className="font-mono">{formatJsPath(effectivePath, false)}</span>
            </button>

            <button
              type="button"
              onClick={() => void triggerCopy(formatJsPath(effectivePath, true), "opt")}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl border border-neutral-200 bg-white px-2.5 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300 cursor-pointer"
              title="Mit sicherem Optional-Chaining (?.) kopieren"
            >
              {copiedKind === "opt" ? (
                <Check className="h-3.5 w-3.5 text-emerald-500" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span>?. Optional-Pfad</span>
            </button>
          </div>
        </div>
      )}

      {/* Search + View Switcher */}
      {rootData !== null && (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-12">
          <div className="relative lg:col-span-5">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              type="search"
              aria-label="In allen verschachtelten Schlüsseln und Werten suchen"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Schlüssel oder Wert tief im JSON suchen (z. B. postalCode, DHL, false)…"
              className="min-h-[44px] w-full rounded-xl border border-neutral-200 bg-white pl-10 pr-3 text-xs sm:text-sm text-neutral-900 focus:border-amber-500 focus:outline-hidden dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100"
            />
          </div>

          <div
            role="tablist"
            aria-label="JSON-Ansichtsmodi"
            className="grid grid-cols-2 gap-1.5 rounded-xl border border-neutral-200/80 bg-neutral-100/80 p-1 sm:grid-cols-4 lg:col-span-7 dark:border-neutral-800 dark:bg-neutral-900/80"
          >
            {[
              { id: "columns" as const, label: "Spalten-Navigator", icon: Columns3 },
              { id: "tree" as const, label: "Baum-Ansicht", icon: Network },
              {
                id: "table" as const,
                label: `Auto-Tabelle (${detectedTables.length})`,
                icon: Table2,
              },
              { id: "typescript" as const, label: "TypeScript-Typen", icon: FileCode2 },
            ].map((tab) => {
              const Icon = tab.icon;
              const active = activeView === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveView(tab.id)}
                  className={`flex min-h-[38px] items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-bold transition-all cursor-pointer ${
                    active
                      ? "bg-white text-amber-700 shadow-2xs dark:bg-neutral-800 dark:text-amber-300"
                      : "text-neutral-600 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Instant Search Hits Bar (jumps directly into nested depth!) */}
      {rootData !== null && searchQuery.trim() && (
        <div className="rounded-2xl border border-amber-500/30 bg-white p-3.5 dark:bg-neutral-900">
          <div className="mb-2 flex items-center justify-between text-xs">
            <span className="font-bold text-neutral-900 dark:text-white">
              Gefundene Pfade für „{searchQuery}“ ({searchMatches.length})
            </span>
            <span className="text-neutral-500">Klicke auf einen Treffer, um genau dorthin zu springen</span>
          </div>
          {searchMatches.length === 0 ? (
            <p className="text-xs text-neutral-500">Kein Schlüssel oder Wert enthält diesen Suchbegriff.</p>
          ) : (
            <div className="grid max-h-48 grid-cols-1 gap-1.5 overflow-y-auto sm:grid-cols-2">
              {searchMatches.map((m, idx) => (
                <button
                  key={`${m.pathLabel}-${idx}`}
                  type="button"
                  onClick={() => {
                    setSelectedPath(m.path);
                    setActiveView("columns");
                  }}
                  className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200/80 bg-neutral-50/80 px-3 py-2 text-left font-mono text-xs hover:border-amber-500/50 hover:bg-amber-500/10 dark:border-neutral-800 dark:bg-neutral-950/70 cursor-pointer"
                >
                  <span className="truncate font-semibold text-neutral-900 dark:text-neutral-100">
                    {m.pathLabel}
                  </span>
                  <span className="shrink-0 rounded bg-amber-500/15 px-1.5 py-0.5 text-[11px] text-amber-800 dark:text-amber-300">
                    {m.preview.slice(0, 26)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* VIEW 1: MILLER COLUMNS NAVIGATOR (Mac Finder Style for Deeply Nested JSON!) */}
      {rootData !== null && activeView === "columns" && (
        <div className="grid min-h-[420px] grid-cols-1 gap-4 lg:grid-cols-12">
          {/* Horizontal Scrollable Columns */}
          <div className="flex gap-3 overflow-x-auto rounded-2xl border border-neutral-200/90 bg-neutral-50/70 p-3 lg:col-span-8 dark:border-neutral-800 dark:bg-neutral-950/60">
            {millerColumns.length === 0 ? (
              <div className="flex w-full items-center justify-center p-6 text-xs text-neutral-500">
                Primitiver Wurzelwert – rechts im Inspektor sichtbar.
              </div>
            ) : (
              millerColumns.map((col) => (
                <div
                  key={col.depth}
                  className="flex min-w-[210px] max-w-[250px] flex-1 flex-col rounded-xl border border-neutral-200/90 bg-white dark:border-neutral-800 dark:bg-neutral-900"
                >
                  <div className="border-b border-neutral-100 px-3 py-2 font-mono text-[11px] font-bold text-neutral-500 dark:border-neutral-800 dark:text-neutral-400 truncate">
                    {col.title}
                  </div>
                  <div className="max-h-[380px] flex-1 space-y-0.5 overflow-y-auto p-1.5">
                    {col.entries.map(({ key, value }) => {
                      const active = col.activeKey === key;
                      const badge = summarizeNodeBadge(value);
                      const hasChildren = value !== null && typeof value === "object";
                      return (
                        <button
                          key={String(key)}
                          type="button"
                          onClick={() => {
                            const nextPath = [...effectivePath.slice(0, col.depth), key];
                            setSelectedPath(nextPath);
                          }}
                          className={`flex min-h-[36px] w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-left font-mono text-xs transition-colors cursor-pointer ${
                            active
                              ? "bg-amber-500 text-white font-bold"
                              : "text-neutral-800 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800"
                          }`}
                        >
                          <span className="truncate">
                            {typeof key === "number" ? `[${key}]` : key}
                          </span>
                          <span className="flex shrink-0 items-center gap-1">
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                active ? "bg-white/20 text-white" : badge.colorClass
                              }`}
                            >
                              {badge.label}
                            </span>
                            {hasChildren && <ChevronRight className="h-3.5 w-3.5 opacity-75" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Right Node Value Inspector */}
          <div className="flex flex-col justify-between rounded-2xl border border-neutral-200/90 bg-white p-4 lg:col-span-4 dark:border-neutral-800 dark:bg-neutral-900">
            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                  Knoten-Inspektor
                </span>
                <button
                  type="button"
                  onClick={() =>
                    void triggerCopy(JSON.stringify(selectedValue, null, 2), "subtree")
                  }
                  className="inline-flex min-h-[34px] items-center gap-1 rounded-lg border border-neutral-200 px-2.5 py-1 text-xs font-semibold text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800 cursor-pointer"
                >
                  {copiedKind === "subtree" ? (
                    <Check className="h-3.5 w-3.5 text-emerald-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                  <span>Teilbaum kopieren</span>
                </button>
              </div>

              <div className="max-h-[350px] overflow-auto rounded-xl border border-neutral-800 bg-zinc-950 p-3">
                <CodeHighlight
                  code={JSON.stringify(selectedValue, null, 2) ?? "null"}
                  language="json"
                />
              </div>
            </div>

            {Array.isArray(selectedValue) && selectedValue.some((x) => isJsonObject(x)) && (
              <button
                type="button"
                onClick={() => {
                  const foundIdx = detectedTables.findIndex(
                    (t) => formatJsPath(t.path) === formatJsPath(effectivePath)
                  );
                  if (foundIdx >= 0) setSelectedTableIdx(foundIdx);
                  setActiveView("table");
                }}
                className="mt-3 inline-flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-xl bg-amber-600 px-3 py-2 text-xs font-bold text-white hover:bg-amber-700 cursor-pointer"
              >
                <Table2 className="h-4 w-4" />
                <span>Dieses Array als flache Tabelle öffnen</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* VIEW 2: INTERACTIVE COLLAPSIBLE TREE */}
      {rootData !== null && activeView === "tree" && (
        <div className="min-h-[420px] max-h-[560px] overflow-auto rounded-2xl border border-neutral-200/90 bg-white p-4 sm:p-5 dark:border-neutral-800 dark:bg-neutral-900">
          <CollapsibleJsonTree
            value={rootData}
            path={[]}
            selectedPath={effectivePath}
            onSelectPath={(p) => setSelectedPath(p)}
            defaultExpandDepth={3}
          />
        </div>
      )}

      {/* VIEW 3: AUTO-FLATTENED TABLE & EXCEL CSV EXPORT */}
      {rootData !== null && activeView === "table" && (
        <div className="min-h-[420px] rounded-2xl border border-neutral-200/90 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900">
          {detectedTables.length === 0 ? (
            <div className="flex min-h-[300px] items-center justify-center text-center text-xs text-neutral-500">
              In dieser Struktur wurde kein Objekt-Array gefunden.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2.5">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-semibold text-neutral-500">Erkannte Listen:</span>
                  {detectedTables.map((tbl, idx) => (
                    <button
                      key={tbl.id}
                      type="button"
                      onClick={() => {
                        setSelectedTableIdx(idx);
                        setSortCol(null);
                      }}
                      className={`min-h-[36px] rounded-xl border px-3 py-1 text-xs font-semibold transition-colors cursor-pointer ${
                        idx === selectedTableIdx
                          ? "border-amber-500 bg-amber-600 text-white"
                          : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200"
                      }`}
                    >
                      {tbl.label}
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="search"
                    aria-label="Tabellenzeilen filtern"
                    value={tableFilter}
                    onChange={(e) => setTableFilter(e.target.value)}
                    placeholder="Tabelle filtern…"
                    className="min-h-[38px] rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-1.5 text-xs text-neutral-900 focus:border-amber-500 focus:outline-hidden dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                  />
                  <button
                    type="button"
                    onClick={exportFlattenedCsv}
                    className="inline-flex min-h-[38px] items-center gap-1.5 rounded-xl bg-emerald-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-emerald-700 cursor-pointer"
                  >
                    <Download className="h-3.5 w-3.5" />
                    <span>Excel-CSV herunterladen</span>
                  </button>
                </div>
              </div>

              <div className="max-h-[430px] overflow-auto rounded-xl border border-neutral-200 dark:border-neutral-800">
                <table className="w-full border-collapse text-left text-xs">
                  <thead className="sticky top-0 z-10 bg-neutral-100 dark:bg-neutral-800">
                    <tr>
                      {flattenedTable.columns.map((col) => (
                        <th
                          key={col}
                          className="border-b border-neutral-200 px-3 py-2.5 font-mono font-bold whitespace-nowrap text-neutral-800 dark:border-neutral-700 dark:text-neutral-100"
                        >
                          <button
                            type="button"
                            onClick={() => {
                              if (sortCol === col) {
                                setSortAsc((a) => !a);
                              } else {
                                setSortCol(col);
                                setSortAsc(true);
                              }
                            }}
                            className="inline-flex items-center gap-1 hover:text-amber-600 dark:hover:text-amber-300 cursor-pointer"
                          >
                            <span>{col}</span>
                            <ArrowUpDown className="h-3 w-3 opacity-60" />
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-200/80 dark:divide-neutral-800">
                    {flattenedTable.rows.map((row, rIdx) => (
                      <tr
                        key={rIdx}
                        className="hover:bg-amber-500/[0.04] dark:hover:bg-amber-500/[0.06]"
                      >
                        {flattenedTable.columns.map((col) => {
                          const cell = row[col];
                          return (
                            <td
                              key={col}
                              className="px-3 py-2 font-mono whitespace-nowrap text-neutral-700 dark:text-neutral-300"
                            >
                              {cell === null || cell === undefined ? (
                                <span className="text-neutral-400">null</span>
                              ) : typeof cell === "boolean" ? (
                                <span
                                  className={`rounded px-1.5 py-0.5 text-[11px] font-bold ${
                                    cell
                                      ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"
                                      : "bg-red-500/15 text-red-700 dark:text-red-300"
                                  }`}
                                >
                                  {String(cell)}
                                </span>
                              ) : (
                                String(cell)
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW 4: GENERATED TYPESCRIPT INTERFACES */}
      {rootData !== null && activeView === "typescript" && (
        <div className="min-h-[420px] rounded-2xl border border-neutral-800 bg-zinc-950 p-4 text-neutral-100">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-neutral-800 pb-3">
            <div className="text-xs text-neutral-400">
              Abgeleitete TypeScript-Definitionen für{" "}
              <code className="font-mono font-bold text-amber-300">
                {formatJsPath(effectivePath)}
              </code>{" "}
              (inkl. optionaler Felder über Array-Elemente hinweg)
            </div>
            <button
              type="button"
              onClick={() => void triggerCopy(generatedTs, "ts")}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-xl bg-amber-600 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-amber-700 cursor-pointer"
            >
              {copiedKind === "ts" ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <Copy className="h-3.5 w-3.5" />
              )}
              <span>TypeScript kopieren</span>
            </button>
          </div>
          <div className="max-h-[430px] overflow-auto">
            <CodeHighlight code={generatedTs} language="typescript" />
          </div>
        </div>
      )}
    </div>
  );
}
